import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  Area, Bar, Brush, CartesianGrid, Cell, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis
} from 'recharts';
import { fmtMoney } from '../../../Documents/format';
import { CHART } from '../../constants';
import {
  axisLabel, deltaPct, fmtCompact, fmtDelta, fmtInt, fmtPct, monthDiff, monthEnd, monthLabel, pointLabel, prevMonth, share
} from '../../format';
import { journalApi } from '../../journalApi';
import { useLoad } from '../../hooks/useLoad';
import { ExpiryPoint, Granularity, JournalFilters, RecordPoint, TimelineBy, TimelineResponse } from '../../types';
import {
  AXIS_TICK, BORDER, ChartCard, ChartSkeleton, NoData, SeriesLegend, SeriesToggle, Stat, StatStrip, TipBox, TipRow, TooltipInjected, tooltipPoint
} from './chartKit';
import MovementChart from './MovementChart';

interface Props {
  filters: JournalFilters;
  /** Фильтры в виде строки - ключ запросов */
  paramsKey: string;
  active: boolean;
  reloadToken: number;
  onPatch: (patch: Partial<JournalFilters>) => void;
}

const MODES: Array<{ value: TimelineBy; label: string; hint: string }> = [
  { value: 'record', label: 'Ввод записей', hint: 'Что и когда вносили аптеки: сумма по дате записи с разбивкой по остатку срока на момент ввода' },
  { value: 'expiry', label: 'Окончание сроков', hint: 'Когда истекает срок годности у товара в журнале: сумма по месяцу окончания' },
  { value: 'movement', label: 'Движение', hint: 'История изменений: внесено, правки, удаления и остаток журнала на конец каждого дня (месяца)' }
];

const HEIGHT = 340;
const BRUSH_FROM = 40;

const toggleIn = (list: string[], key: string) => (list.indexOf(key) !== -1 ? list.filter(k => k !== key) : list.concat(key));

// ===== Ввод записей =====

const RECORD_STACK: SeriesToggle[] = [
  { key: 'expiredAtRecordSum', label: 'Уже просрочено при вводе', color: CHART.expired },
  { key: 'd30AtRecordSum', label: 'До 30 дней', color: CHART.d30 },
  { key: 'd90AtRecordSum', label: '31–90 дней', color: CHART.d90 },
  { key: 'laterSum', label: 'Более 90 дней и без срока', color: CHART.later }
];
const RECORD_LINES: SeriesToggle[] = [
  { key: 'count', label: 'Записей (правая ось)', color: CHART.count, line: true },
  { key: 'prevSum', label: 'Прошлый месяц, тот же день', color: CHART.prev, line: true, dashed: true }
];
const CUM_LINES: SeriesToggle[] = [
  { key: 'cumSum', label: 'Накопительно', color: CHART.cum },
  { key: 'prevCumSum', label: 'Прошлый месяц накопительно', color: CHART.prev, line: true, dashed: true }
];

const RecordTip: React.FC<TooltipInjected<RecordPoint> & { granularity?: Granularity; prevLabel?: string | null }> = props => {
  const p = tooltipPoint(props);
  if (!p) return null;
  const granularity = props.granularity || 'day';
  const rows: TipRow[] = [
    { label: 'Сумма', value: fmtMoney(p.sum), color: CHART.sum, bold: true, hint: `накопительно ${fmtMoney(p.cumSum)}` },
    { label: 'Записей', value: fmtInt(p.count) },
    { label: 'Аптек / товаров', value: `${fmtInt(p.pharmacies)} / ${fmtInt(p.products)}` },
    { label: 'Количество, уп.', value: fmtInt(p.qty) },
    { label: 'Уже просрочено при вводе', value: fmtMoney(p.expiredAtRecordSum), color: CHART.expired, divider: true, hint: p.sum ? fmtPct(share(p.expiredAtRecordSum, p.sum)) : undefined },
    { label: 'До 30 дней', value: fmtMoney(p.d30AtRecordSum), color: CHART.d30, hint: p.sum ? fmtPct(share(p.d30AtRecordSum, p.sum)) : undefined },
    { label: '31–90 дней', value: fmtMoney(p.d90AtRecordSum), color: CHART.d90, hint: p.sum ? fmtPct(share(p.d90AtRecordSum, p.sum)) : undefined },
    { label: 'Более 90 дней и без срока', value: fmtMoney(p.laterSum), color: CHART.later }
  ];
  if (props.prevLabel && p.prevSum !== undefined) {
    const d = deltaPct(p.cumSum, p.prevCumSum);
    rows.push(
      { label: `${props.prevLabel}, тот же день`, value: fmtMoney(p.prevSum), color: CHART.prev, divider: true, hint: `${fmtInt(p.prevCount)} зап.` },
      { label: 'Накопительно к тому же дню', value: fmtMoney(p.prevCumSum), hint: d === null ? undefined : `сейчас ${fmtDelta(d)}` }
    );
  }
  return (
    <TipBox
      width={300} title={pointLabel(p.key, granularity)} rows={rows}
      footer={granularity === 'month' ? 'Клик по столбцу - открыть месяц по дням' : undefined}
    />
  );
};

const RecordChart: React.FC<{ data: Extract<TimelineResponse, { by: 'record' }>; onPatch: Props['onPatch'] }> = ({ data, onPatch }) => {
  const [view, setView] = useState<'bars' | 'cum'>('bars');
  const [hidden, setHidden] = useState<string[]>([]);
  const toggle = useCallback((key: string) => setHidden(h => toggleIn(h, key)), []);
  const show = (key: string) => hidden.indexOf(key) === -1;
  const points = data.points;
  const daily = data.granularity === 'day';
  const prev = data.previous;
  const prevLabel = prev ? monthLabel(prev.month) : null;

  const stats = useMemo<Stat[]>(() => {
    const t = points.reduce((acc, p) => ({
      sum: acc.sum + p.sum, count: acc.count + p.count, qty: acc.qty + p.qty,
      expired: acc.expired + p.expiredAtRecordSum, d30: acc.d30 + p.d30AtRecordSum, d90: acc.d90 + p.d90AtRecordSum,
      withData: acc.withData + (p.count ? 1 : 0)
    }), { sum: 0, count: 0, qty: 0, expired: 0, d30: 0, d90: 0, withData: 0 });
    const peak = points.reduce<RecordPoint | null>((best, p) => (!best || p.sum > best.sum ? p : best), null);
    const unit = daily ? 'дн.' : 'мес.';
    const out: Stat[] = [
      { label: 'Внесено за период', value: fmtMoney(t.sum), hint: `${fmtInt(t.count)} зап. · ${fmtInt(t.qty)} уп.` },
      {
        label: daily ? 'В среднем за день' : 'В среднем за месяц', value: fmtMoney(points.length ? t.sum / points.length : 0),
        hint: `${daily ? 'дней' : 'месяцев'} с вводом: ${t.withData} из ${points.length}`
      },
      { label: 'Пик ввода', value: peak && peak.sum ? fmtMoney(peak.sum) : '-', hint: peak && peak.sum ? `${pointLabel(peak.key, data.granularity)} · ${fmtInt(peak.count)} зап.` : `нет ${unit}` },
      {
        label: 'Внесено с остатком до 90 дней', value: fmtMoney(t.expired + t.d30 + t.d90), color: CHART.d30,
        hint: `${fmtPct(share(t.expired + t.d30 + t.d90, t.sum))} суммы`,
        tooltip: 'Записи, у которых на дату ввода срок уже истёк или оставалось не более 90 дней'
      },
      { label: 'Уже просрочено при вводе', value: fmtMoney(t.expired), color: CHART.expired, hint: `${fmtPct(share(t.expired, t.sum))} суммы` }
    ];
    if (prev && points.length) {
      const last = points[points.length - 1];
      const d = deltaPct(last.cumSum, last.prevCumSum);
      out.push({
        label: `К ${monthLabel(prev.month).toLowerCase()}`, value: d === null ? 'нет данных' : fmtDelta(d),
        color: d === null ? undefined : d >= 0 ? '#15803d' : '#b91c1c',
        hint: `тот же период: ${fmtMoney(last.prevCumSum ?? 0)}`,
        tooltip: `Сравнение с теми же днями прошлого месяца. Весь прошлый месяц: ${fmtMoney(prev.sum)}`
      });
    }
    return out;
  }, [points, daily, prev, data.granularity]);

  if (!points.length) return <NoData height={HEIGHT} />;
  const legend = view === 'bars'
    ? RECORD_STACK.concat(daily && prev ? RECORD_LINES : RECORD_LINES.slice(0, 1))
    : (daily && prev ? CUM_LINES : CUM_LINES.slice(0, 1));
  const openMonth = (item: { payload?: unknown }) => {
    const p = item.payload as RecordPoint | undefined;
    if (!daily && p && p.count) onPatch({ month: p.key });
  };

  return (
    <Box>
      <StatStrip items={stats} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <SeriesLegend items={legend} hidden={hidden} onToggle={toggle} />
        <ToggleButtonGroup
          size="small" exclusive value={view} onChange={(_, v) => { if (v) setView(v); }}
          sx={{ height: 26, mb: 1, '& .MuiToggleButton-root': { px: 1, py: 0, fontSize: '0.72rem', textTransform: 'none' } }}
        >
          <ToggleButton value="bars">По {daily ? 'дням' : 'месяцам'}</ToggleButton>
          <ToggleButton value="cum">Накопительно</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ height: HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="key" tick={AXIS_TICK} tickFormatter={k => axisLabel(String(k), data.granularity)} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={14} />
            <YAxis yAxisId="left" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} />
            {view === 'bars' && (
              <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} tickFormatter={v => fmtInt(Number(v))} axisLine={false} tickLine={false} width={44} hide={!show('count')} />
            )}
            <ChartTooltip content={<RecordTip granularity={data.granularity} prevLabel={prevLabel} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            {view === 'bars' && RECORD_STACK.map(s => (show(s.key) ? (
              <Bar
                key={s.key} yAxisId="left" dataKey={s.key} stackId="r" fill={s.color} isAnimationActive={false} maxBarSize={40}
                onClick={openMonth} style={{ cursor: daily ? 'default' : 'pointer' }}
              />
            ) : null))}
            {view === 'bars' && show('count') && (
              <Line yAxisId="right" dataKey="count" type="monotone" stroke={CHART.count} strokeWidth={2} dot={false} isAnimationActive={false} />
            )}
            {view === 'bars' && daily && prev && show('prevSum') && (
              <Line yAxisId="left" dataKey="prevSum" type="monotone" stroke={CHART.prev} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
            )}
            {view === 'cum' && show('cumSum') && (
              <Area yAxisId="left" dataKey="cumSum" type="monotone" stroke={CHART.cum} fill={CHART.cum} fillOpacity={0.12} strokeWidth={2} isAnimationActive={false} />
            )}
            {view === 'cum' && daily && prev && show('prevCumSum') && (
              <Line yAxisId="left" dataKey="prevCumSum" type="monotone" stroke={CHART.prev} strokeWidth={1.5} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
            )}
            {points.length > BRUSH_FROM && (
              <Brush key={points.length} dataKey="key" height={22} stroke="#94a3b8" travellerWidth={8} startIndex={Math.max(0, points.length - BRUSH_FROM)} tickFormatter={k => axisLabel(String(k), data.granularity)} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// ===== Окончание сроков =====

/** Цвет месяца окончания: прошедшие - красный, текущий - оранжевый, 1–3 месяца - жёлтый, дальше - синий */
function expiryColor(diff: number): string {
  if (diff < 0) return CHART.expired;
  if (diff === 0) return CHART.d30;
  if (diff <= 3) return CHART.d90;
  return CHART.sum;
}

const EXPIRY_KEY: Array<{ label: string; color: string }> = [
  { label: 'Срок истёк', color: CHART.expired },
  { label: 'Текущий месяц', color: CHART.d30 },
  { label: '1–3 месяца', color: CHART.d90 },
  { label: 'Позже', color: CHART.sum }
];

const ExpiryTip: React.FC<TooltipInjected<ExpiryPoint> & { today?: string; total?: number }> = props => {
  const p = tooltipPoint(props);
  if (!p || !props.today) return null;
  const diff = monthDiff(props.today, p.key);
  const status = diff < 0 ? `срок истёк ${-diff} мес. назад` : diff === 0 ? 'истекает в текущем месяце' : `через ${diff} мес.`;
  return (
    <TipBox
      width={290} title={monthLabel(p.key)} subtitle={status}
      rows={[
        { label: 'Сумма', value: fmtMoney(p.sum), color: expiryColor(diff), bold: true, hint: props.total ? `${fmtPct(share(p.sum, props.total))} суммы со сроком` : undefined },
        { label: 'Записей', value: fmtInt(p.count) },
        { label: 'Аптек / товаров', value: `${fmtInt(p.pharmacies)} / ${fmtInt(p.products)}` },
        { label: 'Количество, уп.', value: fmtInt(p.qty) },
        { label: 'Накопительно по этот месяц', value: fmtMoney(p.cumSum), color: CHART.cum, divider: true, hint: props.total ? fmtPct(share(p.cumSum, props.total)) : undefined }
      ]}
      footer="Клик по столбцу - отфильтровать журнал по этому месяцу окончания"
    />
  );
};

const ExpiryChart: React.FC<{ data: Extract<TimelineResponse, { by: 'expiry' }>; onPatch: Props['onPatch'] }> = ({ data, onPatch }) => {
  const [showCum, setShowCum] = useState(true);
  const points = data.points;
  const today = data.today;
  const total = useMemo(() => points.reduce((s, p) => s + p.sum, 0), [points]);

  const stats = useMemo<Stat[]>(() => {
    const sumWhere = (test: (diff: number) => boolean) => points.reduce((acc, p) => {
      if (!test(monthDiff(today, p.key))) return acc;
      return { sum: acc.sum + p.sum, count: acc.count + p.count };
    }, { sum: 0, count: 0 });
    const expired = sumWhere(d => d < 0);
    const current = sumWhere(d => d === 0);
    const next3 = sumWhere(d => d >= 1 && d <= 3);
    const year = sumWhere(d => d >= 0 && d <= 11);
    const later = sumWhere(d => d > 11);
    const peak = points.filter(p => monthDiff(today, p.key) >= 0).reduce<ExpiryPoint | null>((b, p) => (!b || p.sum > b.sum ? p : b), null);
    const item = (label: string, v: { sum: number; count: number }, color?: string, tooltip?: string): Stat => ({
      label, value: fmtMoney(v.sum), color, hint: `${fmtInt(v.count)} зап. · ${fmtPct(share(v.sum, total))}`, tooltip
    });
    return [
      item('Срок уже истёк', expired, CHART.expired),
      item('Истекает в этом месяце', current, CHART.d30),
      item('Через 1–3 месяца', next3, '#b45309'),
      item('В ближайшие 12 месяцев', year, undefined, 'С текущего месяца по 11-й следующий включительно'),
      item('Позже 12 месяцев', later, CHART.later),
      { label: 'Самый «тяжёлый» месяц', value: peak ? monthLabel(peak.key) : '-', hint: peak ? fmtMoney(peak.sum) : undefined, tooltip: 'Месяц (из ещё не наступивших и текущего) с наибольшей суммой истекающего товара' }
    ];
  }, [points, today, total]);

  const brushStart = useMemo(() => {
    const idx = points.findIndex(p => p.key >= today.slice(0, 7));
    return Math.max(0, (idx === -1 ? points.length : idx) - 6);
  }, [points, today]);

  if (!points.length) return <NoData height={HEIGHT} text="Нет записей с указанным сроком годности" />;
  const currentKey = points.find(p => p.current)?.key;
  const pick = (item: { payload?: unknown }) => {
    const p = item.payload as ExpiryPoint | undefined;
    if (p && p.count) onPatch({ expiryFrom: `${p.key}-01`, expiryTo: monthEnd(p.key), bucket: '' });
  };

  return (
    <Box>
      <StatStrip items={stats} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        {EXPIRY_KEY.map(k => (
          <Box key={k.label} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.74rem', color: '#334155' }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: k.color }} />{k.label}
          </Box>
        ))}
        <SeriesLegend items={[{ key: 'cumSum', label: 'Накопительно (правая ось)', color: CHART.cum, line: true }]} hidden={showCum ? [] : ['cumSum']} onToggle={() => setShowCum(v => !v)} />
      </Box>
      <Box sx={{ height: HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 16, right: 8, left: 4, bottom: 0 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="key" tick={AXIS_TICK} tickFormatter={k => axisLabel(String(k), 'month')} axisLine={{ stroke: BORDER }} tickLine={false} minTickGap={14} />
            <YAxis yAxisId="left" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} />
            <YAxis yAxisId="right" orientation="right" tick={AXIS_TICK} tickFormatter={v => fmtCompact(Number(v))} axisLine={false} tickLine={false} width={64} hide={!showCum} />
            <ChartTooltip content={<ExpiryTip today={today} total={total} />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
            {currentKey && (
              <ReferenceLine yAxisId="left" x={currentKey} stroke="#ea580c" strokeDasharray="3 3" label={{ value: 'сейчас', position: 'top', fontSize: 11, fill: '#ea580c' }} />
            )}
            <Bar yAxisId="left" dataKey="sum" isAnimationActive={false} maxBarSize={36} onClick={pick} style={{ cursor: 'pointer' }}>
              {points.map(p => <Cell key={p.key} fill={expiryColor(monthDiff(today, p.key))} />)}
            </Bar>
            {showCum && (
              <Line yAxisId="right" dataKey="cumSum" type="monotone" stroke={CHART.cum} strokeWidth={2} dot={false} isAnimationActive={false} />
            )}
            {points.length > BRUSH_FROM && (
              <Brush
                key={points.length} dataKey="key" height={22} stroke="#94a3b8" travellerWidth={8}
                startIndex={Math.min(brushStart, points.length - 1)} endIndex={Math.min(points.length - 1, brushStart + 30)}
                tickFormatter={k => axisLabel(String(k), 'month')}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

// ===== Вид «Динамика» =====

const TimelineView: React.FC<Props> = ({ filters, paramsKey, active, reloadToken, onPatch }) => {
  const [mode, setMode] = useState<TimelineBy>('record');
  const loader = (by: TimelineBy) => (active && mode === by
    ? (silent: boolean, signal: AbortSignal) => journalApi.timeline(by, filters, silent, signal)
    : null);
  const record = useLoad(loader('record'), `record|${paramsKey}`, reloadToken, 'Не удалось построить динамику ввода');
  const expiry = useLoad(loader('expiry'), `expiry|${paramsKey}`, reloadToken, 'Не удалось построить распределение по срокам');
  const movement = useLoad(loader('movement'), `movement|${paramsKey}`, reloadToken, 'Не удалось построить движение журнала');
  const current = mode === 'record' ? record : mode === 'expiry' ? expiry : movement;
  const data = current.data;
  const modeInfo = MODES.find(m => m.value === mode) || MODES[0];

  const subtitle = useMemo(() => {
    if (mode === 'expiry') return `${modeInfo.hint}. Записи без корректного срока не учитываются.`;
    if (mode === 'movement') {
      const period = filters.month ? `за ${monthLabel(filters.month).toLowerCase()} по дате действия` : 'за всё время по дате действия';
      return `${modeInfo.hint}; ${period}.`;
    }
    const period = filters.month ? `${monthLabel(filters.month).toLowerCase()} по дням, пунктир - ${monthLabel(prevMonth(filters.month)).toLowerCase()}` : 'все периоды по месяцам';
    return `${modeInfo.hint}; ${period}.`;
  }, [mode, modeInfo, filters.month]);

  return (
    <ChartCard
      wide title="Динамика журнала" subtitle={subtitle}
      loading={current.loading} error={current.error?.text} onRetry={current.retry}
      actions={(
        <ToggleButtonGroup
          size="small" exclusive value={mode} onChange={(_, v: TimelineBy | null) => { if (v) setMode(v); }}
          sx={{ height: 30, '& .MuiToggleButton-root': { px: 1.25, py: 0, fontSize: '0.76rem', fontWeight: 600, textTransform: 'none' } }}
        >
          {MODES.map(m => <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>)}
        </ToggleButtonGroup>
      )}
    >
      {mode === 'movement' && filters.picks.manager && (
        <Alert severity="info" icon={false} sx={{ py: 0, mb: 1, fontSize: '0.78rem' }}>
          В истории изменений менеджера нет: фильтр «Менеджер» применяется по товарам этого менеджера, поэтому итоги могут немного расходиться с таблицей.
        </Alert>
      )}
      {!data && !current.error && <ChartSkeleton height={HEIGHT + 120} />}
      {data && data.by === 'record' && <RecordChart data={data} onPatch={onPatch} />}
      {data && data.by === 'expiry' && <ExpiryChart data={data} onPatch={onPatch} />}
      {data && data.by === 'movement' && <MovementChart data={data} height={HEIGHT} />}
      {data && (
        <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', mt: 1 }}>
          Легенда переключает ряды. Фильтры панели над журналом применяются ко всем графикам.
        </Typography>
      )}
    </ChartCard>
  );
};

export default React.memo(TimelineView);
